import type { IInjector } from '@paperbits/common/injection/IInjector';
import type TTQuery from '../../../../cms/client/TTQuery';
import * as ko from 'knockout';
import type { ICmsContentPermalinkResolver } from '../../../../cms/content/ICmsContentPermalinkResolver';
import type { CmsContentContract, CmsContentsListContract } from '../../../../cms/content/ko/CmsContentContract';
import { BundledSettingsProvider } from '../../../../configuration/bundledSettingsProvider';
import type { ICustomPermalinkResolver } from '../../../../media/ICustomPermalinkResolver';
import { ReplyCardModel } from '../../../reply-card/replyCardModel';
import { ReplyCardViewModel, type ReplyCardViewModelInjectables } from '../../../reply-card/ko/replyCardViewModel';
import { CardsWallEndpointType, type QueryInput, makeQuery, overrideQueryInput } from '../../cardsWallUtils';
import {
  bindingContextClassName,
  containerClassName,
  defaultFilteringDateField,
  featuredCardLayout,
  featuredContentsMultiplier,
  implictReferenceCMSContentType
} from '../../constants';
import { type CardsWallRuntimeModel, CardsWallRuntimeModelBuilder } from './cardsWallExchangeModel';
import { CardsWallRuntimeViewModel, optionsSort } from './cardsWallRuntimeViewModel';
import { endOfMonth } from 'date-fns'; // TODO(date-fns)
import type { CustomRouter } from '../../../../routing/customRouter';
import { FilterParameters } from './cardsWallFilterParameters';
import { addDelegatedEventListener } from '../../../../utils/delegate';
import { reducerWithBlackList, reducerWithSubset } from '../../../../utils/blacklists';
import type { SelectTypeOption } from '../../../../utils/types';
import type { CategoryLabel } from '../../../../common/categories/categoryLabel';
import { CategoryLabels } from '../../../../common/categories/categoryLabels';
import type { CardLayout } from '../../../../common/cardLayouts/cardLayout';
import { CardLayouts } from '../../../../common/cardLayouts/cardLayouts';
import { CareersEventApiClient } from '../../../../api/CareersEventApiClient';
import type { ICMSRuntimeClient } from '../../../../cms/client/ICMSRuntimeClient';

export interface SearchApiConfig {
  endpoint: string;
}

export class CardsWallRuntimeElement extends HTMLElement {

  private injected: ReplyCardViewModelInjectables;
  private injectionDependenciesResolved: boolean;

  private gridContainer: HTMLElement;
  private bindingContainer: HTMLElement;
  private mutationObserver: MutationObserver;
  private viewModel: CardsWallRuntimeViewModel;
  private runtimeModel: CardsWallRuntimeModel;
  private pager: HTMLElement;
  private apiClient: ICMSRuntimeClient;
  private searchTimeout: any;
  private wrapperElements: () => NodeListOf<HTMLElement>;
  private router: CustomRouter;
  private filtersSubscriptions: ko.Subscription[] = [];
  private dropdownsObserver: MutationObserver;

  constructor() {
    super();
    this.injectionDependenciesResolved = false;
    // this.settingsProvider = BundledSettingsProvider.newInstance();
    this.mutationObserver = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          setTimeout(() => this.resizeAllMasonryItems());
          break;
        }
      }
    });
    this.wrapperElements = () => this.querySelectorAll<HTMLElement>(
`.contents,\
.more.pager,\
.empty,\
.cards-wall-filters.newsroom,\
.cards-wall-filters.insights > .filters-container,\
.cards-wall-filters.minisite > .filters-container,\
.cards-wall-filters.impacts > .filters-container`
    );
    this.categoryFilterClick = this.categoryFilterClick.bind(this);
    this.filterText = this.filterText.bind(this);
    this.pagerClick = this.pagerClick.bind(this);
    this.resizeAllMasonryItems = this.resizeAllMasonryItems.bind(this);
    this.dropdownsObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes') {
          document.body.style.overflow = (<HTMLElement>mutation.target).classList.contains('show') ? 'hidden' : '';
        }
      }
    });
  }

  private async resolveInjectionDependencies(): Promise<void> {
    const globalInjector: IInjector = await window['globalInjector']; // TODO(injector)
    this.router = globalInjector.resolve<CustomRouter>('router'); // TODO(injector)
    this.injected = {
      mediaPermalinkResolver: globalInjector.resolve<ICustomPermalinkResolver>('mediaPermalinkResolver'),
      cmsContentPermalinkResolver: globalInjector.resolve<ICmsContentPermalinkResolver>('cmsContentPermalinkResolver')
    };
  }

  static get observedAttributes(): string[] {
    return ['params'];
  }

  public attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (name === 'params') {
      if (oldValue !== newValue && newValue?.length) {
        try {
          this.runtimeModel = CardsWallRuntimeModelBuilder.fromSerialized(newValue, true);
        } catch {
          console.error('Unable to parse "params" for "cards-wall".');
        }
        if (this.runtimeModel) {
          if (this.runtimeModel.model.debug) {
            console.log(`[Cards Wall] Deserialized model from "params" attribute: `, this.runtimeModel);
          }
          this.overrideRuntimeModelAndApplyBindings();
        }
      }
    }
  }

  private async overrideRuntimeModelAndApplyBindings(): Promise<void> {
    if (!this.injectionDependenciesResolved) {
      await this.resolveInjectionDependencies();
      this.injectionDependenciesResolved = true;
    }
    const model = this.runtimeModel?.model || ({} as { filtersEnabled?: boolean, staticItems?: boolean });
    const isOverridable: boolean = model.filtersEnabled && !model.staticItems;
    if (this.router && isOverridable) {
      const params: [string, string][] = this.router.getSearchParams();
      overrideQueryInput(this.runtimeModel.queryInput, new FilterParameters(params));
    }
    this.applyBindings();
  }

  private async onDatePickerChange(): Promise<void> {
    const viewModel = this.viewModel;
    const datePickerDate: Date = viewModel.datePickerDate();
    const queryInput: QueryInput = viewModel.queryInput();
    if (datePickerDate) {
      const startDate: string = datePickerDate.toISOString();
      const endDate: string = endOfMonth(datePickerDate).toISOString();
      if (queryInput.dateField === defaultFilteringDateField && queryInput.startDate === startDate && queryInput.endDate === endDate) {
        return;
      }
      queryInput.dateField = defaultFilteringDateField;
      queryInput.startDate = startDate;
      queryInput.endDate = endDate;
    } else {
      if (!queryInput.dateField && !queryInput.startDate && !queryInput.endDate) {
        return;
      }
      delete queryInput.dateField;
      delete queryInput.startDate;
      delete queryInput.endDate;
    }
    queryInput.offset = 0;
    this.pager.setAttribute('disabled', 'disabled');
    const contentsList: CmsContentsListContract = await this.next(queryInput);
    const hasNextPage: boolean = contentsList.paginationInfo.nextOffset > 0;
    viewModel.paginationEnabled(hasNextPage);
    for (const el of Array.from(this.querySelectorAll('.cards-wall-grid-container > .cards-wall-item'))) {
      el.remove();
    }
    viewModel.cards.removeAll();
    viewModel.cards.push(...await this.contentContractsToViewModels(contentsList.list));
    this.pager.removeAttribute('disabled');
  }

  private async applyBindings(): Promise<void> {
    const runtimeModel = this.runtimeModel;
    if (!this.viewModel) {
      this.viewModel = new CardsWallRuntimeViewModel(runtimeModel).withDatePickerConfig();
    }
    const viewModel = this.viewModel;
    if (viewModel) {
      const model = runtimeModel.model;
      viewModel.darkTheme(model.darkTheme);
      viewModel.preloadedItems(runtimeModel.preloadedItems);
      viewModel.precomputedItemsTotal(runtimeModel.precomputedItemsTotal);
      viewModel.filtersEnabled(model.filtersEnabled);
      viewModel.queryInput({ ...runtimeModel.queryInput });
      if (!model.staticItems) {
        // since next() may be executed in parallel - avoid lazy init
        await this.initApiClients();
        const requests: [
            Promise<CmsContentsListContract>,
            Promise<CmsContentsListContract | undefined>,
            Promise<CmsContentsListContract | undefined>
        ] = [
            this.next(runtimeModel.queryInput),
            this.next(runtimeModel.featuredQueryInput), // might be undefined
            this.next(runtimeModel.selectedQueryInput), // might be undefined
        ];
        const [contents, featuredContents, selectedContents]: CmsContentsListContract[] = await Promise.all(requests);
        const hasNextPageIfPaginationEnabled: boolean = model.paginationEnabled && contents.paginationInfo.nextOffset > 0;
        let selectedCardModels: ReplyCardViewModel[];
        if (selectedContents) {
          selectedCardModels = await this.contentContractsToViewModels(selectedContents.list, featuredContents?.list || undefined);
        }
        const cardsViewModels: ReplyCardViewModel[] = await this.contentContractsToViewModels(contents.list, selectedContents?.list ? undefined : featuredContents?.list);
        if (selectedCardModels) {
          viewModel.cards([...selectedCardModels, ...cardsViewModels]);
        } else {
          viewModel.cards([...cardsViewModels]);
        }
        viewModel.paginationEnabled(hasNextPageIfPaginationEnabled);
      } else {
        viewModel.cards([]);
        viewModel.paginationEnabled(model.paginationEnabled);
      }
      const subscriptions = this.filtersSubscriptions;
      const filtersType = model.filtersType;
      if (['insights', 'minisite', 'impacts'].includes(filtersType)) {
        if (['insights', 'minisite'].includes(filtersType)) {
          const categories = model.categories;
          const categoriesSet: CategoryLabel[] = categories?.length
            ? categories.reduce((acc, c) => acc.concat(CategoryLabels.get(c)), [])
            : CategoryLabels.get();
          const categoriesOptions: SelectTypeOption[] = categoriesSet.map(c => ({ label: c.name, value: c.key }));
          viewModel.categoriesOptions(categoriesOptions.sort(optionsSort));
        }

        await this.initApiClients();
        if (this.apiClient.getContentType) {
          if (['insights', 'impacts'].includes(filtersType)) {
            const contentTypeSets = await this.apiClient.getContentType(implictReferenceCMSContentType);

            const industriesSet = contentTypeSets.data.metaKeys?.filter((metaKey: any) => metaKey.key === "industries")?.[0]?.optionValues || 0;
            const industries = model.industries;
            const industriesOptions: SelectTypeOption[] = industries?.length
              ? reducerWithSubset(industriesSet, industries)
              : reducerWithBlackList(industriesSet, 'industries');
            viewModel.industriesOptions(industriesOptions.sort(optionsSort));

            if (filtersType === 'insights') {
              const servicesSet = contentTypeSets.data.metaKeys?.filter((metaKey: any) => metaKey.key === "services")?.[0]?.optionValues || 0;
              const services = model.services;
              const servicesOptions: SelectTypeOption[] = services?.length
                ? reducerWithSubset(servicesSet, services)
                : reducerWithBlackList(servicesSet, 'services');
              viewModel.servicesOptions(servicesOptions.sort(optionsSort));
            }

            if (filtersType === 'impacts') {
              const impactsSet = contentTypeSets.data.metaKeys?.filter((metaKey: any) => metaKey.key === "business_impact")?.[0]?.optionValues || 0;
              const impactsOptions: SelectTypeOption[] = reducerWithBlackList(impactsSet, 'impacts');
              viewModel.impactsOptions(impactsOptions.sort(optionsSort));
            }
          }
        }

        if (['insights', 'minisite'].includes(filtersType) && subscriptions.length === 0) { // TODO split
          subscriptions.push(
            viewModel.categories.subscribe(this.insightsFiltersListener, this),
            viewModel.industries.subscribe(this.insightsFiltersListener, this),
            viewModel.services.subscribe(this.insightsFiltersListener, this),
            viewModel.clearAllInsightsFilters.subscribe(
              (oldValue: boolean) => oldValue && this.filterText(undefined), // clearAllInsightsFilters clicked
              this,
              'beforeChange'
            )
          );
        } else if (filtersType === 'impacts' && subscriptions.length === 0) {
          subscriptions.push(
            viewModel.impacts.subscribe(this.impactsFiltersListener, this),
            viewModel.industries.subscribe(this.impactsFiltersListener, this),
            viewModel.clearAllImpactsFilters.subscribe(
                (oldValue: boolean) => oldValue && this.filterText(undefined), // clearAllImpactsFilters clicked
                this,
                'beforeChange'
            )
          );
        }
      } else if (filtersType === 'newsroom') {
        if (subscriptions.length === 0) {
          subscriptions.push(viewModel.datePickerValue.subscribe(this.onDatePickerChange, this));
          subscriptions.push(viewModel.clearAllNewsroomFilters.subscribe(
            (oldValue: boolean) => oldValue && this.filterText(undefined), // clearAllNewsroomFilters clicked
            this,
            'beforeChange'
          ));
        }
      }
    }
    for (const w of this.wrapperElements()) {
      if (w !== null && !ko.contextFor(w)) {
        ko.applyBindings(viewModel, w);
      }
    }
  }

  private async insightsFiltersListener(): Promise<void> {
    const viewModel = this.viewModel;
    const queryInput: QueryInput = viewModel.queryInput();
    const categories = viewModel.categories();
    queryInput.sections = categories.length
        ? categories.map(c => c.value)
        : this.runtimeModel.queryInput.sections;
    const industries = viewModel.industries();
    queryInput.industries = industries.length
        ? industries.map(i => i.value)
        : this.runtimeModel.queryInput.industries;
    const services = viewModel.services();
    queryInput.services = services.length
        ? services.map(s => s.value)
        : this.runtimeModel.queryInput.services;
    queryInput.offset = 0;
    this.pager.setAttribute('disabled', 'disabled');
    const contentsList: CmsContentsListContract = await this.next(queryInput);
    const hasNextPage: boolean = contentsList.paginationInfo.nextOffset > 0;
    viewModel.paginationEnabled(hasNextPage);
    for (const el of Array.from(this.querySelectorAll('.cards-wall-grid-container > .cards-wall-item'))) {
      el.remove();
    }
    viewModel.cards.removeAll();
    viewModel.cards.push(...await this.contentContractsToViewModels(contentsList.list));
    this.pager.removeAttribute('disabled');
  }

  private async impactsFiltersListener(): Promise<void> {
    const viewModel = this.viewModel;
    const queryInput: QueryInput = viewModel.queryInput();
    const impacts = viewModel.impacts();
    queryInput.impacts = impacts.length
        ? impacts.map(c => c.value)
        : this.runtimeModel.queryInput.impacts;
    const industries = viewModel.industries();
    queryInput.industries = industries.length
        ? industries.map(i => i.value)
        : this.runtimeModel.queryInput.industries;
    queryInput.offset = 0;
    this.pager.setAttribute('disabled', 'disabled');
    const contentsList: CmsContentsListContract = await this.next(queryInput);
    const hasNextPage: boolean = contentsList.paginationInfo.nextOffset > 0;
    viewModel.paginationEnabled(hasNextPage);
    for (const el of Array.from(this.querySelectorAll('.cards-wall-grid-container > .cards-wall-item'))) {
      el.remove();
    }
    viewModel.cards.removeAll();
    viewModel.cards.push(...await this.contentContractsToViewModels(contentsList.list));
    this.pager.removeAttribute('disabled');
  }

  public connectedCallback(): void {
    this.bindingContainer = this.querySelector(`.${bindingContextClassName}`) || <HTMLElement>this.firstElementChild;
    this.gridContainer = this.querySelector(`.${containerClassName}`) || <HTMLElement>this.bindingContainer.firstElementChild;

    this.mutationObserver.observe(this.gridContainer, { attributes: false, childList: true, subtree: true });

    window.addEventListener('resize', this.resizeAllMasonryItems);

    setTimeout(() => {
      this.pager = this.querySelector('.more.pager');

      for (const el of this.querySelectorAll('.filter-dropdown')) {
        this.dropdownsObserver.observe(el, {
          attributes: true,
          attributeFilter: ['class']
        });
      }
    });

    addDelegatedEventListener(this, 'click', '.more.pager', this.pagerClick);

    addDelegatedEventListener(this, 'click', '.filter-item', (e: MouseEvent) => {
      this.categoryFilterClick(<HTMLElement>e.target);
    });

    addDelegatedEventListener(this, 'mousedown', '.newsroom [data-dismiss]', async (_e: MouseEvent) => {
      const viewModel = this.viewModel;
      viewModel.datePickerDate(viewModel.tempDatePickerDate());
    });

    addDelegatedEventListener(this, 'mousedown', '.insights [data-dismiss]', async (e: MouseEvent) => {
      const viewModel = this.viewModel;
      const filterType = (<HTMLElement>e.target).closest<HTMLElement>('.filter')?.dataset?.['filterType'];
      switch (filterType) {
        case 'categories':
          viewModel.categories(viewModel.tempCategories());
          break;
        case 'industries':
          viewModel.industries(viewModel.tempIndustries());
          break;
        case 'services':
          viewModel.services(viewModel.tempServices());
          break;
      }
    });

    addDelegatedEventListener(this, 'mousedown', '.minisite [data-dismiss]', async () => {
      const viewModel = this.viewModel;
      viewModel.categories(viewModel.tempCategories());
    });

    addDelegatedEventListener(this, 'mousedown', '.impacts [data-dismiss]', async (e: MouseEvent) => {
      const viewModel = this.viewModel;
      const filterType = (<HTMLElement>e.target).closest<HTMLElement>('.filter')?.dataset?.['filterType'];
      switch (filterType) {
        case 'impacts':
          viewModel.impacts(viewModel.tempImpacts());
          break;
        case 'industries':
          viewModel.industries(viewModel.tempIndustries());
          break;
      }
    });

    addDelegatedEventListener(this, 'input', '.filter--search > input', async (e: InputEvent) => {
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => this.filterText((<HTMLInputElement>e.target).value), 400);
      const target: HTMLInputElement = (<HTMLInputElement>e.target);
      target.parentElement.classList.toggle('searched', !!target.value);
    });

    addDelegatedEventListener(this, 'click', '.filter--search__submit', async (e: InputEvent) => {
      const parent: HTMLElement = (<HTMLElement>e.target).parentElement;
      const value: string = parent.querySelector('input').value;
      if (value) {
        clearTimeout(this.searchTimeout);
        this.filterText(value);
        parent.classList.add('searched');
      } else if (parent.closest('.newsroom')) {
        parent.classList.toggle('open');
      }
    });

    addDelegatedEventListener(this, 'click', '.filter--search__reset', async (e: InputEvent) => {
      const parent: HTMLElement = (<HTMLElement>e.target).parentElement;
      clearTimeout(this.searchTimeout);
      this.viewModel.searchInputValue(undefined);
      this.filterText(undefined);
      parent.classList.remove('searched');
    });
  }

  private destroyBindings(): void {
    for (const w of this.wrapperElements()) {
      if (w !== null && !ko.contextFor(w)) {
        ko.cleanNode(w);
      }
    }
  }

  public disconnectedCallback(): void {
    this.destroyBindings();
    this.mutationObserver.disconnect();
    this.dropdownsObserver.disconnect();

    window.removeEventListener('resize', this.resizeAllMasonryItems);

    for (const el of this.filtersSubscriptions) {
      el.dispose();
    }
  }

  private resizeAllMasonryItems(): void {
    const items = this.gridContainer.querySelectorAll('.cards-wall-item');
    if (this.runtimeModel?.model.debug) {
      console.log('[Cards Wall] Number of wall items before resizing: ', items.length);
    }
    for (const it of items) {
      this.resizeMasonryItem(<HTMLElement>it);
    }
  }

  private resizeMasonryItem(item: HTMLElement): void {
    /* Reference: https://w3bits.com/css-grid-masonry/ */
    /* Get the grid object, its row-gap, and the size of its implicit rows */
    const gridComputedStyle = window.getComputedStyle(this.gridContainer);
    const rowGap: number = Number.parseInt(gridComputedStyle.getPropertyValue('row-gap'));
    const marginGap: number = Number.parseInt(window.getComputedStyle(item).getPropertyValue('margin-bottom'));
    const rowHeight: number = Number.parseInt(gridComputedStyle.getPropertyValue('grid-auto-rows'));

    const cardElement: Element = item.querySelector('.reply-card');
    if (cardElement) {
      /*
      * Spanning for any brick = S
      * Grid's row-gap = G
      * Size of grid's implicitly create row-track = R
      * Height of item content = H
      * Net height of the item = H1 = H + G
      * Net height of the implicit row-track = T = G + R
      * S = H1 / T
      */
      const rowSpan = Math.ceil((cardElement.getBoundingClientRect().height + rowGap) / (rowHeight + rowGap));

      /* Set the spanning as calculated above (S) */
      item.style.gridRowEnd = `span ${Math.ceil(rowSpan + marginGap / rowGap)}`;
    } else {
      console.warn('[Cards Wall] Item has no "Reply Card" child.', item);
    }
  }

  private async contentContractsToViewModels(contracts: CmsContentContract[], featuredContracts?: CmsContentContract[]): Promise<ReplyCardViewModel[]> {
    const featuredViewModels: Promise<ReplyCardViewModel>[] = [];
    if (featuredContracts?.length) {
      featuredContracts.sort((c1: CmsContentContract, c2: CmsContentContract): number => {
        if (c1.id === c2.id) {
          return 0;
        }
        return this.runtimeModel.featuredQueryInput.whitelist.indexOf(c1.id) - this.runtimeModel.featuredQueryInput.whitelist.indexOf(c2.id);
      });
      const featuredLayout: CardLayout = CardLayouts.get(featuredCardLayout)[0];
      for (const featured of featuredContracts) {
        const model = await ReplyCardModel.contentContractToModel(featured, this.injected, this.runtimeModel.siteKey, true);
        model.layout = featuredLayout.key;
        featuredViewModels.push(ReplyCardViewModel.modelToViewModel(
            model,
            this.injected,
            null,
            {
              site: {
                key: this.runtimeModel.siteKey,
                language: this.runtimeModel.siteLanguage
              }
            }
        ));
      }
    }
    const viewModels: Promise<ReplyCardViewModel>[] = [];
    for (const contract of contracts) {
      const model = await ReplyCardModel.contentContractToModel(contract, this.injected, this.runtimeModel.siteKey, true);
      if (this.viewModel.shortLayout()) {
        model.layout = model.layout.includes('card-wall-hover') || model.layout.includes('card-wall-abstract')
            ? 'card-wall-hover-16-9-default' : 'card-wall-nohover-16-9-default';
      }
      viewModels.push(ReplyCardViewModel.modelToViewModel(
        model, this.injected, null,
        { site: { key: this.runtimeModel.siteKey, language: this.runtimeModel.siteLanguage } }
      ));
    }
    for (let i = featuredViewModels.length - 1; i >= 0 ; i--) {
        if (i < 2 && i * featuredContentsMultiplier < this.runtimeModel.queryInput.limit) {
          viewModels.splice(i * featuredContentsMultiplier, 0, featuredViewModels[i]);
        } else {
          viewModels.push(featuredViewModels[i]);
        }
    }
    return await Promise.all(viewModels);
  }

  public async pagerClick(): Promise<void> {
    this.pager.setAttribute('disabled', 'disabled');
    const queryInput: QueryInput = this.viewModel.queryInput();
    queryInput.offset += queryInput.limit;
    const contentsList: CmsContentsListContract = await this.next(queryInput);
    const hasNextPage: boolean = contentsList.paginationInfo.nextOffset > 0;
    this.viewModel.paginationEnabled(hasNextPage);
    if (contentsList.list?.length) {
      this.viewModel.cards.push(...await this.contentContractsToViewModels(contentsList.list));
    }
    this.pager.removeAttribute('disabled');
  }

  public async filterText(text: string): Promise<void> {
    this.pager.setAttribute('disabled', 'disabled');
    const queryInput: QueryInput = this.viewModel.queryInput();
    queryInput.searchInput = text;
    queryInput.offset = 0;
    const contentsList: CmsContentsListContract = await this.next(queryInput);
    const hasNextPage: boolean = contentsList.paginationInfo.nextOffset > 0;
    this.viewModel.paginationEnabled(hasNextPage);
    for (const el of Array.from(this.querySelectorAll('.cards-wall-grid-container > .cards-wall-item'))) {
      el.remove();
    }
    this.viewModel.cards.removeAll();
    this.viewModel.cards.push(...await this.contentContractsToViewModels(contentsList.list));
    this.pager.removeAttribute('disabled');
  }

  public async toggleSearch(): Promise<void> {
    this.querySelector('.filter-search').classList.toggle('collapsed');
  }

  public async categoryFilterClick(item: HTMLElement): Promise<void> {
    const category = item.getAttribute('data-filter-category');
    const isActive = item.classList.contains('active');

    for (const filter of Array.from(item.parentElement.children)) {
      filter.classList.remove('active');
    }

    if (!isActive) {
      item.classList.add('active');
    }

    this.pager.setAttribute('disabled', 'disabled');
    const queryInput: QueryInput = this.viewModel.queryInput();
    queryInput.offset = 0;
    queryInput.sections = [];
    if (!isActive) {
      queryInput.sections.push(category);
      if (category === 'news') {
        queryInput.sections.push('acknowledgment');
      }
    } else {
      queryInput.sections = this.runtimeModel.queryInput.sections;
    }
    const contentsList: CmsContentsListContract = await this.next(queryInput);
    const hasNextPage: boolean = contentsList.paginationInfo.nextOffset > 0;
    this.viewModel.paginationEnabled(hasNextPage);
    for (const el of Array.from(this.querySelectorAll('.cards-wall-grid-container > .cards-wall-item'))) {
      el.remove();
    }
    this.viewModel.cards.removeAll();
    this.viewModel.cards.push(...await this.contentContractsToViewModels(contentsList.list));
    this.pager.removeAttribute('disabled');
  }

  private async initApiClients(): Promise<void> {
    if (!this.apiClient) {
      const provider = BundledSettingsProvider.newInstance();
      if (this.viewModel.endpointType() === CardsWallEndpointType.CAREERS_EVENT) {
        const apiConfig: SearchApiConfig = provider.getSettingSync<SearchApiConfig>('search');
        this.apiClient = CareersEventApiClient.build({ endpoint: apiConfig.endpoint });
      } else {
        const globalInjector: IInjector = await window['globalInjector'];
        this.apiClient = globalInjector.resolve<ICMSRuntimeClient>('apiClient');
      }
    }
  }

  private async next(queryInput?: QueryInput): Promise<CmsContentsListContract> {
    if (!queryInput) {
      return undefined;
    }
    await this.initApiClients();
    if (this.viewModel.endpointType() === CardsWallEndpointType.CAREERS_EVENT) {
      const searchParamsCountry = new URLSearchParams(window.parent.location.search).getAll('country');
      if (searchParamsCountry.length) {
        queryInput.country = searchParamsCountry;
      }
    }
    const query: TTQuery = makeQuery(queryInput);
    let result: { data: CmsContentsListContract };
    try {
      result = await this.apiClient.listContents<CmsContentContract>(query);
    } catch {
      console.error('[Cards Wall] It was not possible to retrieve contents.', this);
    }
    return result?.data || {
      list: [],
      paginationInfo: {
        limit: queryInput.limit,
        nextOffset: 0,
        startOffset: queryInput.offset,
        total: 0
      }
    };
  }
}
